import { useQuery } from '@tanstack/react-query';
import {
  type AdvancesResponse,
  formatMoney,
  INVOICE_ADVANCE_STATE_LABELS,
  type InvoiceAdvanceLike,
  type InvoiceAdvanceState,
  invoiceAdvanceSummary,
  fromCents,
  type InvoiceDirection,
  type InvoiceKind,
  type InvoiceListItem,
  type InvoiceOrigin,
  type InvoicesResponse,
  type InvoiceState,
  invoiceStateLabel,
  italianToday,
  addDays,
  JOB_INVOICE_STATUS_LABELS,
  JOB_INVOICE_STATUSES,
  type JobInvoiceStatus,
  type JobInvoiceStatusResponse,
  toCents,
} from '@k-management/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { DataGrid, type GridColumn } from '../../components/DataGrid.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate, formatTimestamp } from '../../lib/format.js';
import { jobKeys } from '../jobs/jobs-ui.js';
import { GestifyInvoicesDialog } from './GestifyInvoicesDialog.js';
import { CreditNoteMark, creditNoteRow, InvoiceStatePill, treasuryKeys } from './treasury-ui.js';
import { SMALL_RESIDUAL_LABEL, withSmallResidual, WriteOffAllButton } from './WriteOffAllButton.js';
import { useListFilters } from '../../lib/list-filters.js';

/** The advance of the bank (1.82): any, or one state of it. */
type AdvanceFilter = 'all' | 'any' | InvoiceAdvanceState;

const ADVANCE_SHORT: Record<InvoiceAdvanceState, string> = {
  'in-attesa': 'in attesa del cliente',
  'da-restituire': 'da restituire',
  'da-sistemare': 'da sistemare',
  insoluto: 'insoluto',
  chiuso: 'chiuso',
};

const ADVANCE_PILLS: Record<InvoiceAdvanceState, string> = {
  'in-attesa': 'info',
  'da-restituire': 'warn',
  'da-sistemare': 'bad',
  insoluto: 'bad',
  chiuso: 'ok',
};

const JOB_STATUS_PILLS: Record<JobInvoiceStatus, string> = {
  da_imputare: 'warn',
  parziale: 'info',
  imputata: 'ok',
  esclusa: 'muted',
};

/** «small»: a residual up to the limit of the abbuono (1.81). */
type StateFilter = 'open' | 'all' | 'small' | 'cancelled' | InvoiceState;
type SelfBillingFilter = 'without' | 'only' | 'all';
/** «da usare»: the credit notes with something left to link or refund (1.58). */
type KindFilter = 'all' | InvoiceKind | 'da_usare';

const TITLES: Record<InvoiceDirection, { title: string; intro: string }> = {
  emessa: {
    title: 'Fatture attive',
    intro: 'Le fatture emesse ai clienti: quello che K-Rev deve incassare.',
  },
  ricevuta: {
    title: 'Fatture passive',
    intro: 'Le fatture ricevute dai fornitori: quello che K-Rev deve pagare.',
  },
};

/**
 * Tesoreria › Fatture attive or passive (06-07/10/2026): the invoices of
 * Gestify, with what is left to collect or pay and what is overdue. The
 * self-billing stay out of the list and of the totals unless asked for; the
 * credit notes can be shown alone (07/10/2026), the totals following.
 */
export function InvoicesPage({ direction }: { direction: InvoiceDirection }) {
  const { title, intro } = TITLES[direction];
  usePageTitle(title);
  const navigate = useNavigate();
  const canWrite = useCan('tesoreria.write');
  const canJobs = useCan('commesse.read');
  // The filters stay while the browser is open: found again coming back to the list (1.61).
  const { filters, setFilter, reset, changed, changedClass } = useListFilters(
    `invoices-${direction}`,
    {
      state: 'open' as StateFilter,
      year: 'all' as string,
      selfBilling: 'without' as SelfBillingFilter,
      kind: 'all' as KindFilter,
      origin: 'all' as InvoiceOrigin | 'all',
      // Without an item of the plan Base (1.76): the subjects to complete in the Anagrafica.
      reclass: 'all' as 'all' | 'missing',
      // Anticipated by the bank (1.82): all of them, or in one state of their advance.
      advance: 'all' as AdvanceFilter,
      // Where they stand with the commesse (2.47): «Da imputare» and the others.
      jobs: 'all' as 'all' | JobInvoiceStatus,
    },
  );
  const { state, year, selfBilling, kind, origin, reclass, advance, jobs } = filters;
  const setState = (value: StateFilter) => setFilter('state', value);
  const setYear = (value: string) => setFilter('year', value);
  const setSelfBilling = (value: SelfBillingFilter) => setFilter('selfBilling', value);
  const setKind = (value: KindFilter) => setFilter('kind', value);
  const setOrigin = (value: InvoiceOrigin | 'all') => setFilter('origin', value);
  // The items of the situation at 1 January count in what is open (1.48).
  const [reading, setReading] = useState(false);
  const invoices = useQuery({
    queryKey: treasuryKeys.invoices,
    queryFn: () => apiRequest<InvoicesResponse>('/treasury/invoices'),
  });

  // The advances of the bank on the issued invoices (1.82), shared with Anticipi fatture.
  const advances = useQuery({
    queryKey: ['treasury', 'advances'],
    queryFn: () => apiRequest<AdvancesResponse>('/treasury/advances'),
    enabled: direction === 'emessa',
  });
  const advancesOf = new Map<string, InvoiceAdvanceLike[]>();
  for (const item of advances.data?.advances ?? []) {
    for (const invoice of item.invoices) {
      advancesOf.set(invoice.invoiceId, [
        ...(advancesOf.get(invoice.invoiceId) ?? []),
        {
          movementId: item.movementId,
          accountCode: item.accountCode,
          date: item.date,
          reference: item.reference,
          advanced: invoice.advanced,
          closures: invoice.closures,
          open: invoice.open,
        },
      ]);
    }
  }
  // Where the invoices stand with the commesse (2.47): only those that concern them are listed.
  const jobStatuses = useQuery({
    queryKey: [...jobKeys.all, 'invoice-status'],
    queryFn: () => apiRequest<JobInvoiceStatusResponse>('/jobs/invoice-status'),
    enabled: canJobs,
  });
  const jobStatusOf = new Map(
    (jobStatuses.data?.invoices ?? []).map((item) => [item.invoiceId, item.status]),
  );

  const advanceState = (item: InvoiceListItem): InvoiceAdvanceState | null => {
    const found = advancesOf.get(item.id);
    return found ? invoiceAdvanceSummary(item, found).state : null;
  };

  // The totals follow the choice on the self-billing too.
  const ofDirection = (invoices.data?.invoices ?? []).filter(
    (item) =>
      item.direction === direction &&
      (selfBilling === 'all' || item.selfBilling === (selfBilling === 'only')) &&
      (kind === 'all' ||
        item.kind === kind ||
        (kind === 'da_usare' && item.kind === 'nota_credito' && item.state !== 'saldata')) &&
      (origin === 'all' || item.origin === origin) &&
      (reclass === 'all' || !item.reclassCode) &&
      (advance === 'all' ||
        (advance === 'any' ? advancesOf.has(item.id) : advanceState(item) === advance)) &&
      (!canJobs || jobs === 'all' || jobStatusOf.get(item.id) === jobs),
  );
  const years = [...new Set(ofDirection.map((item) => item.date.slice(0, 4)))].sort().reverse();
  const ofYear = ofDirection.filter((item) => year === 'all' || item.date.startsWith(year));
  const rows =
    state === 'small'
      ? withSmallResidual(ofYear)
      : ofYear.filter(
          (item) =>
            state === 'all' ||
            (state === 'cancelled'
              ? Boolean(item.cancelled)
              : state === 'open'
                ? item.state !== 'saldata'
                : item.state === state && !item.cancelled),
        );

  const issued = direction === 'emessa';
  const today = italianToday();
  const in30 = addDays(today, 30);
  const sum = (items: InvoiceListItem[], amount: (item: InvoiceListItem) => string) =>
    fromCents(items.reduce((total, item) => total + toCents(amount(item)), 0));
  const open = ofDirection.filter((item) => item.state !== 'saldata');
  const tiles = [
    { label: issued ? 'Da incassare' : 'Da pagare', value: sum(open, (item) => item.residual) },
    { label: 'Scaduto', value: sum(open, (item) => item.overdue), bad: true },
    {
      label: 'In scadenza nei prossimi 30 giorni',
      value: sum(
        open.filter((item) => item.nextDue && item.nextDue >= today && item.nextDue <= in30),
        (item) => item.residual,
      ),
    },
    { label: 'Documenti aperti', value: null, count: open.length },
  ];

  const columns: GridColumn<InvoiceListItem>[] = [
    {
      key: 'number',
      label: 'Numero',
      required: true,
      value: (item) => item.number,
      render: (item) => (
        <>
          <CreditNoteMark kind={item.kind} />
          <strong>{item.number}</strong>
          {item.origin === 'apertura' && (
            <span className="pill info" title="Partita aperta nella situazione iniziale">
              situazione iniziale
            </span>
          )}
        </>
      ),
    },
    {
      key: 'date',
      label: 'Data',
      value: (item) => item.date,
      render: (item) => formatDate(item.date),
    },
    {
      key: 'counterparty',
      label: issued ? 'Cliente' : 'Fornitore',
      // The aliases of its subject too (1.73): the search finds the other names.
      value: (item) => [item.counterpartyName, ...(item.aliases ?? [])].join(' '),
      render: (item) => (
        <>
          {item.counterpartyName}
          {item.kind === 'proforma' && (
            <span className="pill muted">
              {item.replaced ? 'pro-forma sostituita' : 'pro-forma'}
            </span>
          )}
          {item.origin === 'manuale' && item.kind !== 'proforma' && (
            <span className="pill muted">inserita a mano</span>
          )}
          {item.selfBilling && <span className="pill self-billing">autofattura</span>}
          {item.splitPayment && <span className="pill info">split payment</span>}
          {!item.subjectId && <small className="muted block">non in Anagrafica</small>}
        </>
      ),
    },
    {
      key: 'total',
      label: 'Totale',
      align: 'right',
      value: (item) => Number(item.total),
      render: (item) => formatMoney(item.total),
    },
    {
      key: 'residual',
      label: issued ? 'Da incassare' : 'Da pagare',
      align: 'right',
      value: (item) => Number(item.residual),
      render: (item) => formatMoney(item.residual),
    },
    {
      key: 'nextDue',
      label: 'Prossima scadenza',
      value: (item) => item.nextDue ?? '',
      render: (item) => (item.nextDue ? formatDate(item.nextDue) : '—'),
    },
    ...(issued
      ? [
          {
            key: 'advance',
            label: 'Anticipo',
            hiddenByDefault: true,
            value: (item: InvoiceListItem) => {
              const found = advanceState(item);
              return found ? INVOICE_ADVANCE_STATE_LABELS[found] : '';
            },
            render: (item: InvoiceListItem) => {
              const found = advanceState(item);
              return found ? (
                <span className={`pill ${ADVANCE_PILLS[found]}`}>{ADVANCE_SHORT[found]}</span>
              ) : (
                ''
              );
            },
          } satisfies GridColumn<InvoiceListItem>,
        ]
      : []),
    ...(canJobs
      ? [
          {
            key: 'jobs',
            label: 'Commesse',
            hiddenByDefault: true,
            value: (item: InvoiceListItem) => {
              const found = jobStatusOf.get(item.id);
              return found ? JOB_INVOICE_STATUS_LABELS[found] : '';
            },
            render: (item: InvoiceListItem) => {
              const found = jobStatusOf.get(item.id);
              return found ? (
                <span className={`pill ${JOB_STATUS_PILLS[found]}`}>
                  {JOB_INVOICE_STATUS_LABELS[found].toLowerCase()}
                </span>
              ) : (
                ''
              );
            },
          } satisfies GridColumn<InvoiceListItem>,
        ]
      : []),
    {
      key: 'state',
      label: 'Stato',
      value: (item) => invoiceStateLabel(item.state, item.direction, item.kind),
      render: (item) => (
        <InvoiceStatePill
          state={item.state}
          direction={item.direction}
          kind={item.kind}
          replaced={item.replaced}
          cancelled={item.cancelled}
          overdueDays={item.overdueDays}
        />
      ),
    },
  ];

  const commands: (Command | false)[] = [
    canWrite && {
      key: 'gestify',
      label: 'Aggiorna da Gestify',
      icon: 'upload',
      primary: true,
      onClick: () => setReading(true),
    },
    // Documents entered by hand, only in the ciclo passivo (1.59).
    canWrite &&
      !issued && {
        key: 'manual',
        label: 'Nuovo documento',
        icon: 'plus',
        onClick: () => navigate('/tesoreria/fatture-passive/nuovo'),
      },
  ];
  useCommands(commands.filter((command): command is Command => Boolean(command)));

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>{title}</h1>
          <p className="muted">
            {intro} Importi e scadenze arrivano da Gestify, incassi e pagamenti si segnano qui.
            {invoices.data?.lastImportAt &&
              ` Ultimo aggiornamento da Gestify: ${formatTimestamp(invoices.data.lastImportAt)}.`}
          </p>
        </div>
      </div>
      {invoices.data && (
        <div className="treasury-tiles">
          {tiles.map((tile) => (
            <div key={tile.label} className={tile.bad ? 'treasury-tile bad' : 'treasury-tile'}>
              <span>{tile.label}</span>
              <strong>{tile.value !== null ? formatMoney(tile.value) : tile.count}</strong>
            </div>
          ))}
        </div>
      )}
      {invoices.isPending && <p className="muted">Caricamento…</p>}
      {invoices.isError && <p className="error">{invoices.error.message}</p>}
      {invoices.data && (
        <DataGrid
          id={`invoices-${direction}`}
          filtersChanged={changed}
          onResetFilters={reset}
          columns={columns}
          rows={rows}
          rowKey={(item) => item.id}
          searchPlaceholder={issued ? 'Numero o cliente' : 'Numero o fornitore'}
          empty={
            invoices.data.invoices.length === 0
              ? canWrite
                ? 'Nessuna fattura: leggile con «Aggiorna da Gestify».'
                : 'Nessuna fattura ancora letta da Gestify.'
              : 'Nessuna fattura con questi filtri.'
          }
          rowClassName={(item) =>
            [item.state === 'saldata' ? 'inactive' : '', creditNoteRow(item) ?? '']
              .join(' ')
              .trim() || undefined
          }
          onRowOpen={(item) => navigate(`/tesoreria/fatture/${item.id}`)}
          openOnClick
          toolbar={
            <>
              <select
                className={changedClass('state')}
                aria-label="Stato"
                value={state}
                onChange={(event) => setState(event.target.value as StateFilter)}
              >
                <option value="open">Aperte</option>
                <option value="scaduta">Scadute</option>
                <option value="parziale">
                  {issued ? 'Incassate in parte' : 'Pagate in parte'}
                </option>
                <option value="saldata">{issued ? 'Incassate' : 'Pagate'}</option>
                <option value="small">{SMALL_RESIDUAL_LABEL}</option>
                <option value="cancelled">Annullate</option>
                <option value="all">Tutte</option>
              </select>
              <select
                className={changedClass('year')}
                aria-label="Anno"
                value={year}
                onChange={(event) => setYear(event.target.value)}
              >
                <option value="all">Tutti gli anni</option>
                {years.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
              <select
                className={changedClass('reclass')}
                aria-label="Voce riclassificata"
                value={reclass}
                onChange={(event) => setFilter('reclass', event.target.value as 'all' | 'missing')}
              >
                <option value="all">Tutte le voci</option>
                <option value="missing">Senza voce riclassificata</option>
              </select>
              {issued && (
                <select
                  className={changedClass('advance')}
                  aria-label="Anticipo"
                  value={advance}
                  onChange={(event) => setFilter('advance', event.target.value as AdvanceFilter)}
                >
                  <option value="all">Con e senza anticipo</option>
                  <option value="any">Anticipate</option>
                  <option value="in-attesa">Anticipate · in attesa del cliente</option>
                  <option value="da-restituire">Anticipate · anticipo da restituire</option>
                  <option value="da-sistemare">Anticipate · da sistemare</option>
                  <option value="insoluto">Anticipate · insolute</option>
                  <option value="chiuso">Anticipate · chiuse</option>
                </select>
              )}
              {canJobs && (
                <select
                  className={changedClass('jobs')}
                  aria-label="Commesse"
                  value={jobs}
                  title={
                    jobStatuses.data?.from
                      ? `«Da imputare»: le fatture dal ${formatDate(jobStatuses.data.from)} di clienti e fornitori delle commesse e dei progetti aperti`
                      : undefined
                  }
                  onChange={(event) =>
                    setFilter('jobs', event.target.value as 'all' | JobInvoiceStatus)
                  }
                >
                  <option value="all">Con e senza commesse</option>
                  {JOB_INVOICE_STATUSES.map((item) => (
                    <option key={item} value={item}>
                      Commesse · {JOB_INVOICE_STATUS_LABELS[item].toLowerCase()}
                    </option>
                  ))}
                </select>
              )}
              <select
                className={changedClass('selfBilling')}
                aria-label="Autofatture"
                value={selfBilling}
                onChange={(event) => setSelfBilling(event.target.value as SelfBillingFilter)}
              >
                <option value="without">Autofatture escluse</option>
                <option value="only">Solo autofatture</option>
                <option value="all">Con le autofatture</option>
              </select>
              <select
                className={changedClass('kind')}
                aria-label="Documento"
                value={kind}
                onChange={(event) => setKind(event.target.value as KindFilter)}
              >
                <option value="all">Fatture e note di credito</option>
                <option value="fattura">Solo fatture</option>
                <option value="nota_credito">Solo note di credito</option>
                <option value="da_usare">Note di credito da usare</option>
                {!issued && <option value="proforma">Solo pro-forma</option>}
              </select>
              <select
                className={changedClass('origin')}
                aria-label="Origine"
                value={origin}
                onChange={(event) => setOrigin(event.target.value as InvoiceOrigin | 'all')}
              >
                <option value="all">Con la situazione iniziale</option>
                <option value="gestify">Solo fatture di Gestify</option>
                {!issued && <option value="manuale">Solo inserite a mano</option>}
                <option value="apertura">Solo situazione iniziale</option>
              </select>
              {state === 'small' && <WriteOffAllButton items={rows} />}
            </>
          }
        />
      )}
      {reading && <GestifyInvoicesDialog onClose={() => setReading(false)} />}
    </section>
  );
}
